import { gql, NetworkStatus, useMutation, useQuery } from '@apollo/client'
import { useState } from 'react'

import { currentArtistVar, useGetNewestData } from '@/clients/utoniq-core'
import {
  ArtistBottomTabFragmentDoc,
  ArtistPostsFragmentDoc,
  ArtistPostsQuery,
  ArtistPostsQueryVariables,
  CurrentUserFragmentDoc,
  FetchNewArtistPostsQuery,
  FetchNewArtistPostsQueryVariables,
  LikePost,
  LikePostMutation,
  LikePostMutationVariables,
  PostBoxFragmentDoc,
  PostType,
  UnlikePost,
  UnlikePostMutation,
  UnlikePostMutationVariables,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'

import { Action, State } from './template'

type FilterType = PostType | 'general'

export const useViewModel = makeViewModel<State, Action>(() => {
  /**
   *  TODO:
   * - filterを@connection directiveなどでキャッシュから取得
   * - refetchの際にpostsのキャッシュを一新するロジックの実装
   *  */
  //
  const [filter, setFilter] = useState<FilterType>('general')
  const { router } = useRouter<{ artistId: string }>()
  const artistId = router.query.artistId ?? config.artistId

  const { isFirstLoadRef } = useGetNewestData<
    FetchNewArtistPostsQuery,
    FetchNewArtistPostsQueryVariables
  >({
    query: FETCH_NEW_ARTIST_POSTS,
    variables: {
      artistId,
      resourceType: filter === 'general' ? null : filter,
      pageInput: { limit: 10 },
    },
    identify: { __typename: 'Artist', id: artistId },
    fields: [
      {
        fieldName: 'posts',
        responsePath: 'artist.posts',
        fragment: PostBoxFragmentDoc,
      },
    ],
    onCompleted(data) {
      if (data?.artist) {
        currentArtistVar(data.artist)
      }
    },
  })

  const { data, fetchMore, refetch, networkStatus } = useQuery<
    ArtistPostsQuery,
    ArtistPostsQueryVariables
  >(ARTIST_POSTS, {
    variables: {
      artistId,
      pageInput: { limit: 10 },
    },
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      isFirstLoadRef.current = false
      if (data?.artist) {
        currentArtistVar(data.artist)
      }
    },
  })

  const isNextFetch = !!data?.artist?.posts

  const [likePost] = useMutation<LikePostMutation, LikePostMutationVariables>(
    LikePost
  )

  const [unlikePost] = useMutation<
    UnlikePostMutation,
    UnlikePostMutationVariables
  >(UnlikePost)

  const pageInfo = data?.artist?.posts?.pageInfo

  const handleFilter = (filterType: FilterType) => {
    if (filterType === filter) return
    refetch({
      resourceType: filterType === 'general' ? null : filterType,
      pageInput: {
        cursor: null,
        limit: 10,
      },
    })

    setFilter(filterType)
  }

  const fetchMorePosts = async () => {
    if (pageInfo && pageInfo.hasNextPage) {
      await fetchMore({
        variables: {
          artistId,
          pageInput: {
            cursor: pageInfo.endCursor || null,
            limit: 3,
          },
        },
      })
    }
  }

  const nodes = data?.artist?.posts?.edges?.map(({ node }) => node) || []

  return {
    state: {
      user: data?.user,
      posts: nodes,
      loading: networkStatus === NetworkStatus.loading && !isNextFetch,
      refetching: networkStatus === NetworkStatus.fetchMore,
      filter,
      artistName: data?.artist?.displayName || '',
      hasNextPage: !!pageInfo?.hasNextPage,
      liveStreamId: data?.artist?.currentLive?.id,
    },
    actions: {
      onFilter: handleFilter,
      onFetchMore: fetchMorePosts,
      async onLikePost(postId, isLike, likeCount) {
        if (isLike) {
          await unlikePost({
            variables: { unlikePostPostId: postId },
            optimisticResponse: ({ unlikePostPostId }) => ({
              unlikePost: {
                __typename: 'Post',
                id: unlikePostPostId,
                isLike: false,
                likeCount: --likeCount,
              },
            }),
          })
          return
        }
        await likePost({
          variables: { likePostPostId: postId },
          optimisticResponse: ({ likePostPostId }) => ({
            likePost: {
              __typename: 'Post',
              id: likePostPostId,
              isLike: true,
              likeCount: ++likeCount,
            },
          }),
        })
      },
    },
  }
})

const ARTIST_POSTS = gql`
  query artistPosts(
    $artistId: ID!
    $pageInput: PageInputDate
    $resourceType: PostType
  ) {
    artist(id: $artistId) {
      ...ArtistBottomTab
      id
      displayName
      posts(pageInput: $pageInput, resourceType: $resourceType) {
        ...ArtistPosts
      }
      currentLive {
        id
      }
    }
    user {
      ...CurrentUser
    }
  }
  ${ArtistBottomTabFragmentDoc}
  ${ArtistPostsFragmentDoc}
  ${CurrentUserFragmentDoc}
`

const FETCH_NEW_ARTIST_POSTS = gql`
  query FetchNewArtistPosts(
    $artistId: ID!
    $pageInput: PageInputDate
    $resourceType: PostType
  ) {
    artist(id: $artistId) {
      ...ArtistBottomTab
      id
      posts(pageInput: $pageInput, resourceType: $resourceType) {
        ...ArtistPosts
      }
      currentLive {
        id
      }
    }
  }
  ${ArtistBottomTabFragmentDoc}
  ${ArtistPostsFragmentDoc}
`
